import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import staticEventosEspeciales from '../data/eventos_especiales.json';
import styles from '../css/SecretEventsAdmin.module.css';

const SECRET_PIN = "IUMP2026"; // Contraseña predeterminada

const SecretEventsAdmin = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(() => {
        return sessionStorage.getItem('admin_eventos_auth') === 'true';
    });
    const [pinInput, setPinInput] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    
    const [eventos, setEventos] = useState([]);
    const [editingId, setEditingId] = useState(null);

    const wrapperRef = useRef(null);
    const upcomingRef = useRef(null);
    const hasScrolledRef = useRef(false);

    const getTodayStr = () => {
        const today = new Date();
        const y = today.getFullYear();
        const m = String(today.getMonth() + 1).padStart(2, '0');
        const d = String(today.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const todayStr = getTodayStr();
    const pastEvents = eventos.filter(evt => evt.fecha < todayStr);
    const upcomingEvents = eventos.filter(evt => evt.fecha >= todayStr);

    useEffect(() => {
        if (isAuthenticated && wrapperRef.current && upcomingRef.current && eventos.length > 0) {
            if (!hasScrolledRef.current) {
                setTimeout(() => {
                    const wrapper = wrapperRef.current;
                    const target = upcomingRef.current;
                    if (wrapper && target) {
                        wrapper.scrollTop = target.offsetTop - 5;
                        hasScrolledRef.current = true;
                    }
                }, 100);
            }
        }
    }, [isAuthenticated, eventos]);
    
    // Form fields
    const [titulo, setTitulo] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [fecha, setFecha] = useState('');
    const [hora, setHora] = useState('');
    const [lugar, setLugar] = useState('');
    const [tipo, setTipo] = useState('local');

    // Load events on mount
    useEffect(() => {
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            setEventos(staticEventosEspeciales || []);
            return;
        }

        const local = localStorage.getItem('eventos_especiales');
        if (local) {
            try {
                setEventos(JSON.parse(local));
            } catch (e) {
                setEventos(staticEventosEspeciales || []);
            }
        } else {
            setEventos(staticEventosEspeciales || []);
        }
    }, [staticEventosEspeciales]);

    // Handle Authentication
    const handleAuth = (e) => {
        e.preventDefault();
        if (pinInput === SECRET_PIN) {
            setIsAuthenticated(true);
            sessionStorage.setItem('admin_eventos_auth', 'true');
            setErrorMsg('');
        } else {
            setErrorMsg('Contraseña incorrecta. Inténtelo de nuevo.');
        }
    };

    // Save events to state and localStorage (and physical file if on localhost)
    const saveEvents = (updatedList) => {
        setEventos(updatedList);
        localStorage.setItem('eventos_especiales', JSON.stringify(updatedList));

        // Guardado automático físico si estamos en localhost
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            fetch('http://localhost:3005/api/save-events', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedList),
            })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    console.log("[Localhost] Cambios persistidos en eventos_especiales.json automáticamente.");
                } else {
                    console.error("[Localhost] Error persistiendo cambios en eventos_especiales.json:", data.error);
                }
            })
            .catch(err => {
                console.error("[Localhost] Error de red. ¿Ejecutaste el servidor de desarrollo?", err);
            });
        }
    };

    // Start Edit Event
    const startEdit = (evt) => {
        setEditingId(evt.id);
        setTitulo(evt.titulo);
        setDescripcion(evt.descripcion || '');
        setFecha(evt.fecha);
        setHora(evt.hora || '');
        setLugar(evt.lugar || '');
        setTipo(evt.tipo);
    };

    // Cancel Edit Event
    const cancelEdit = () => {
        setEditingId(null);
        setTitulo('');
        setDescripcion('');
        setFecha('');
        setHora('');
        setLugar('');
        setTipo('local');
    };

    // Form Submit (Add or Edit Event)
    const handleSubmitForm = (e) => {
        e.preventDefault();
        if (!titulo || !fecha) {
            alert('El título y la fecha son campos requeridos.');
            return;
        }

        if (editingId) {
            // Edit Mode
            const updated = eventos.map(evt => {
                if (evt.id === editingId) {
                    return {
                        ...evt,
                        titulo,
                        descripcion,
                        fecha,
                        hora,
                        lugar,
                        tipo
                    };
                }
                return evt;
            });
            
            // Sort by date
            updated.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
            saveEvents(updated);
            setEditingId(null);
            alert('Evento actualizado con éxito.');
        } else {
            // Add Mode
            const newEvent = {
                id: `evt-${Date.now()}`,
                titulo,
                descripcion,
                fecha,
                hora,
                lugar,
                tipo
            };

            const updated = [...eventos, newEvent];
            updated.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
            saveEvents(updated);
            alert('Evento agregado con éxito temporalmente.');
        }

        // Reset form
        setTitulo('');
        setDescripcion('');
        setFecha('');
        setHora('');
        setLugar('');
        setTipo('local');
    };

    // Delete Event
    const handleDeleteEvent = (id) => {
        if (window.confirm('¿Está seguro de que desea eliminar este evento?')) {
            const updated = eventos.filter(evt => evt.id !== id);
            saveEvents(updated);
        }
    };

    // Copy JSON to clipboard
    const copyJsonToClipboard = () => {
        const jsonText = JSON.stringify(eventos, null, 2);
        navigator.clipboard.writeText(jsonText)
            .then(() => alert('¡Código JSON copiado al portapapeles! Pégalo en tu archivo src/data/eventos_especiales.json'))
            .catch(err => console.error('Error al copiar JSON', err));
    };

    // Login View
    if (!isAuthenticated) {
        return (
            <div className={styles.loginContainer}>
                <div className={styles.loginCard}>
                    <img src="/Logo-Iump-Concon.ico" alt="Logo" className={styles.logo} />
                    <h2>Acceso Reservado</h2>
                    <p>Ingrese la contraseña para administrar eventos extraordinarios</p>
                    <form onSubmit={handleAuth}>
                        <input 
                            type="password" 
                            placeholder="Contraseña Secreta" 
                            value={pinInput} 
                            onChange={(e) => setPinInput(e.target.value)}
                            className={styles.inputField}
                            required 
                        />
                        {errorMsg && <p className={styles.errorMsg}>{errorMsg}</p>}
                        <button type="submit" className={styles.btnPrimary}>Ingresar</button>
                    </form>
                    <Link to="/" className={styles.backLink}>Volver al inicio</Link>
                </div>
            </div>
        );
    }

    // Admin View
    return (
        <div className={styles.adminContainer}>
            <header className={styles.adminHeader}>
                <div className={styles.headerInfo}>
                    <img src="/Logo-Iump-Concon.ico" alt="Logo" className={styles.miniLogo} />
                    <h2>Administración de Eventos Extraordinarios</h2>
                </div>
                <Link to="/" className={styles.btnSecondary}>Ver Sitio Público</Link>
            </header>

            <div className={styles.adminGrid}>
                {/* FORMULARIO AGREGAR */}
                <div className={styles.cardForm}>
                    <h3>
                        <i className={editingId ? "fas fa-edit" : "fas fa-plus"}></i>{" "}
                        {editingId ? `Editar Evento` : "Agregar Nuevo Evento"}
                    </h3>
                    <form onSubmit={handleSubmitForm} className={styles.eventForm}>
                        <div className={styles.formGroup}>
                            <label>Título del Evento *</label>
                            <input 
                                type="text" 
                                value={titulo} 
                                onChange={(e) => setTitulo(e.target.value)} 
                                placeholder="Ej. Convención de Coros" 
                                required
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label>Descripción / Detalles</label>
                            <textarea 
                                value={descripcion} 
                                onChange={(e) => setDescripcion(e.target.value)} 
                                placeholder="Ej. Evento especial con invitación abierta..." 
                                rows="3"
                            />
                        </div>

                        <div className={styles.formRow}>
                            <div className={styles.formGroup}>
                                <label>Fecha *</label>
                                <input 
                                    type="date" 
                                    value={fecha} 
                                    onChange={(e) => setFecha(e.target.value)} 
                                    required
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Hora (HH:MM)</label>
                                <input 
                                    type="time" 
                                    value={hora} 
                                    onChange={(e) => setHora(e.target.value)} 
                                />
                            </div>
                        </div>

                        <div className={styles.formRow}>
                            <div className={styles.formGroup}>
                                <label>Lugar</label>
                                <input 
                                    type="text" 
                                    value={lugar} 
                                    onChange={(e) => setLugar(e.target.value)} 
                                    placeholder="Ej. Templo Concón" 
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Tipo de Evento</label>
                                <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
                                    <option value="local">Actividad Local Extraordinaria</option>
                                    <option value="nacional">Corporación Nacional</option>
                                </select>
                            </div>
                        </div>

                        <div className={styles.actionButtons}>
                            <button type="submit" className={styles.btnSubmit}>
                                <i className="fas fa-save"></i> {editingId ? "Guardar Cambios" : "Agregar Evento (Temporal)"}
                            </button>
                            {editingId && (
                                <button type="button" onClick={cancelEdit} className={styles.btnCancel}>
                                    Cancelar
                                </button>
                            )}
                        </div>
                    </form>
                </div>

                {/* LISTA Y EXPORTACIÓN */}
                <div className={styles.cardList}>
                    <h3><i className="fas fa-list"></i> Eventos Guardados</h3>
                    <div ref={wrapperRef} className={styles.eventsTableWrapper}>
                        {eventos.length === 0 ? (
                            <p className={styles.noEventsText}>No hay eventos extraordinarios cargados.</p>
                        ) : (
                            <table className={styles.eventsTable}>
                                <thead>
                                    <tr>
                                        <th>Fecha/Hora</th>
                                        <th>Título</th>
                                        <th>Tipo</th>
                                        <th>Lugar</th>
                                        <th>Acción</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* SECCIÓN PASADOS */}
                                    {pastEvents.length > 0 && (
                                        <tr className={styles.separatorRowPast}>
                                            <td colSpan="5">
                                                <i className="fas fa-history"></i> EVENTOS PASADOS (Desliza hacia arriba)
                                            </td>
                                        </tr>
                                    )}
                                    {pastEvents.map((evt) => (
                                        <tr key={evt.id} className={`${styles.rowPast} ${evt.tipo === 'nacional' ? styles.rowNacional : ''}`}>
                                            <td>
                                                <strong>{evt.fecha}</strong>
                                                <div className={styles.tblTime}>{evt.hora || 'S/H'}</div>
                                            </td>
                                            <td>
                                                <div className={styles.tblTitle}>{evt.titulo}</div>
                                                <div className={styles.tblDesc}>{evt.descripcion}</div>
                                            </td>
                                            <td>
                                                <span className={evt.tipo === 'nacional' ? styles.tblBadgeNacional : styles.tblBadgeLocal}>
                                                    {evt.tipo}
                                                </span>
                                            </td>
                                            <td>{evt.lugar || 'No especificado'}</td>
                                            <td className={styles.actionsCell}>
                                                <button 
                                                    type="button"
                                                    onClick={() => startEdit(evt)} 
                                                    className={styles.btnEdit}
                                                    title="Editar Evento"
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                <button 
                                                    type="button"
                                                    onClick={() => handleDeleteEvent(evt.id)} 
                                                    className={styles.btnDelete}
                                                    title="Eliminar Evento"
                                                >
                                                    <i className="fas fa-trash-alt"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}

                                    {/* SECCIÓN PRÓXIMOS / HOY */}
                                    <tr ref={upcomingRef} className={styles.separatorRowUpcoming}>
                                        <td colSpan="5">
                                            <i className="fas fa-calendar-day"></i> EVENTOS PRÓXIMOS (Hoy en adelante)
                                        </td>
                                    </tr>
                                    {upcomingEvents.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className={styles.noEventsText}>
                                                No hay eventos próximos programados.
                                            </td>
                                        </tr>
                                    ) : (
                                        upcomingEvents.map((evt) => (
                                            <tr key={evt.id} className={evt.tipo === 'nacional' ? styles.rowNacional : ''}>
                                                <td>
                                                    <strong>{evt.fecha}</strong>
                                                    <div className={styles.tblTime}>{evt.hora || 'S/H'}</div>
                                                </td>
                                                <td>
                                                    <div className={styles.tblTitle}>{evt.titulo}</div>
                                                    <div className={styles.tblDesc}>{evt.descripcion}</div>
                                                </td>
                                                <td>
                                                    <span className={evt.tipo === 'nacional' ? styles.tblBadgeNacional : styles.tblBadgeLocal}>
                                                        {evt.tipo}
                                                    </span>
                                                </td>
                                                <td>{evt.lugar || 'No especificado'}</td>
                                                <td className={styles.actionsCell}>
                                                    <button 
                                                        type="button"
                                                        onClick={() => startEdit(evt)} 
                                                        className={styles.btnEdit}
                                                        title="Editar Evento"
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                    <button 
                                                        type="button"
                                                        onClick={() => handleDeleteEvent(evt.id)} 
                                                        className={styles.btnDelete}
                                                        title="Eliminar Evento"
                                                    >
                                                        <i className="fas fa-trash-alt"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className={styles.exportSection}>
                        <div className={styles.exportHeader}>
                            <h3><i className="fas fa-cloud-upload-alt"></i> Exportar para Hostinger</h3>
                            <button onClick={copyJsonToClipboard} className={styles.btnCopy}>
                                <i className="fas fa-copy"></i> Copiar JSON para Producción
                            </button>
                        </div>
                        <p className={styles.helpText}>
                            Para hacer que los cambios sean permanentes para todos los usuarios en Hostinger:
                            <br />
                            1. Haz clic en **Copiar JSON para Producción** arriba.
                            <br />
                            2. Abre tu código local, ve al archivo **src/data/eventos_especiales.json**, borra todo su contenido y pega lo que acabas de copiar.
                            <br />
                            3. Ejecuta **npm run build** en tu terminal local y sube los nuevos archivos compilados a tu servidor.
                        </p>
                        <textarea 
                            readOnly 
                            value={JSON.stringify(eventos, null, 2)} 
                            className={styles.jsonTextarea}
                            onClick={(e) => e.target.select()}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SecretEventsAdmin;
